import React, { useMemo, useState } from 'react';
import { Pagination } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { SbTable, SbTag, SbTooltip } from 'uiCore';
import { PROFILE_PAGE_SIZE, PROFILE_PAGE_SIZE_OPTIONS } from 'constants/profileManagement.constants';
import {
  EditIconStyled,
  ProfileTableScrollAreaStyled,
  ProfilePaginationFooterStyled,
} from './ProfileManagementV2.styles';

const TOOLTIP_NO_ACCESS = "This profile has no access to this module.";
const TOOLTIP_EDIT_PERMISSION = "This profile has edit permission for this module.";

const ProfileTableV2 = ({ profiles, loading, onFeatureClick }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PROFILE_PAGE_SIZE);

  const featureColumns = useMemo(() => {
    if (!profiles.length) return [];

    const firstProfile = profiles[0];
    const features = firstProfile?.features || [];

    return [...features]
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map((feature) => ({
        title: feature.feature_name,
        key: feature.feature_id,
        align: 'center',
        render: (_, record) => {
          const feat = record.features?.find(
            (f) => f.feature_id === feature.feature_id,
          );
          const accessible = feat?.accessible;

          if (!accessible) {
            return (
              <SbTooltip content={TOOLTIP_NO_ACCESS}>
                <span>—</span>
              </SbTooltip>
            );
          }

          return (
            <SbTooltip content={TOOLTIP_EDIT_PERMISSION}>
              <EditIconStyled
                onClick={(e) => {
                  e.stopPropagation();
                  onFeatureClick({
                    profile: record,
                    featureId: feature.feature_id,
                    featureName: feature.feature_name,
                  });
                }}
              >
                <EditOutlined />
              </EditIconStyled>
            </SbTooltip>
          );
        },
      }));
  }, [profiles, onFeatureClick]);

  const columns = useMemo(
    () => [
      {
        title: 'Profile Name',
        dataIndex: 'role_name',
        key: 'role_name',
        render: (text, record) => (
          <>
            {text || '-'}
            {record.default_role && (
              <SbTag color="processing">Default</SbTag>
            )}
          </>
        ),
      },
      ...featureColumns,
    ],
    [featureColumns],
  );

  // Paginate the data manually
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return profiles.slice(start, start + pageSize);
  }, [profiles, currentPage, pageSize]);

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  return (
    <>
      {/* Scrollable table area — no built-in pagination */}
      <ProfileTableScrollAreaStyled>
        <SbTable
          columns={columns}
          dataSource={paginatedData}
          loading={loading}
          rowKey={(record) => record.role_id}
          pagination={false}
          autoFilters={false}
          size="middle"
        />
      </ProfileTableScrollAreaStyled>

      {/* Fixed pagination footer — always at the bottom */}
      <ProfilePaginationFooterStyled>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={profiles.length}
          showSizeChanger
          pageSizeOptions={PROFILE_PAGE_SIZE_OPTIONS}
          onChange={handlePaginationChange}
          onShowSizeChange={handlePaginationChange}
          size="small"
        />
      </ProfilePaginationFooterStyled>
    </>
  );
};

export default ProfileTableV2;
